import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { PieChart, Pie, Cell } from "recharts";
import { useNavigate } from "react-router-dom";
import { useToast } from "./Toast";

interface MetricData {
  value: number | string;
  label: string;
  color: string;
}

interface MetricCardProps {
  title: string;
  total: number | string | null;
  subtitle: string;
  data: MetricData[];
  path?: string;
  onClick?: () => void;
  hideByDefault?: boolean;
  financeViewPermission?: boolean;
  canView?: boolean;
  currency?: boolean;
  expectedValue?: number | null;
  difference?: number | null;
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  total,
  subtitle,
  data,
  path,
  onClick,
  hideByDefault = false,
  financeViewPermission = true,
  canView = true,
  currency = false,
  expectedValue = null,
  difference = null,
}) => {
  const navigate = useNavigate();
  const [isHidden, setIsHidden] = useState(hideByDefault);
  const { showToast } = useToast();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallDesktop = useMediaQuery(theme.breakpoints.down("lg"));
  // Helper function to mask numbers
  const maskValue = (value: number | string): string => {
    if (isHidden || !financeViewPermission) {
      const valueStr = value?.toString();
      return valueStr?.replace(/\d/g, "*") || "";
    }
    return value?.toString() || "0";
  };

  const handleToggleVisibility = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click when clicking the eye icon
    if (financeViewPermission) {
      setIsHidden(!isHidden);
    } else {
      showToast("You don't have permission to view this details", "warning");
    }
  };

  const handleCardClick = () => {
    if (path && canView) {
      navigate(path);
      onClick?.();
    }
  };

  return (
    <Card
      onClick={handleCardClick}
      sx={{
        cursor: path ? "pointer" : "default",
        "&:hover": {
          backgroundColor: path ? "rgba(0, 0, 0, 0.04)" : "transparent",
        },
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardContent
        sx={{
          flex: 1,
          padding: isMobile ? 1.5 : isSmallDesktop ? 1.5 : 2,
          "&:last-child": {
            paddingBottom: isMobile ? 1.5 : isSmallDesktop ? 1.5 : 2,
          },
        }}
      >
        {/* Header with title and visibility toggle */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={1.5}
          minHeight="24px"
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 500,
              fontSize: isMobile
                ? "0.8rem"
                : isSmallDesktop
                ? "0.85rem"
                : "0.95rem",
              lineHeight: 1.2,
              wordBreak: "break-word",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: isMobile ? 2 : 1,
              WebkitBoxOrient: "vertical",
              flex: 1,
              mr: hideByDefault ? 0.5 : 0,
            }}
          >
            {title}
          </Typography>
          {hideByDefault && (
            <IconButton
              size="small"
              onClick={handleToggleVisibility}
              sx={{
                color: "text.secondary",
                flexShrink: 0,
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              {isHidden ? (
                <Visibility fontSize="small" />
              ) : (
                <VisibilityOff fontSize="small" />
              )}
            </IconButton>
          )}
        </Box>

        {/* Content layout - responsive stacking */}
        <Box
          display="flex"
          flexDirection={
            isMobile || (isSmallDesktop && data.length > 2) ? "column" : "row"
          }
          alignItems={
            isMobile || (isSmallDesktop && data.length > 2)
              ? "flex-start"
              : "flex-start"
          }
          justifyContent="space-between"
          gap={isMobile ? 1.5 : 0.5}
        >
          {/* Main value section */}
          <Box
            flex={
              isMobile || (isSmallDesktop && data.length > 2) ? "none" : "auto"
            }
            minWidth={0}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: isMobile
                  ? "1rem"
                  : isSmallDesktop
                  ? "0.7rem"
                  : "0.8rem",
                fontWeight: 600,
                wordBreak: "break-all",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace:
                  isMobile || (isSmallDesktop && data.length > 2)
                    ? "normal"
                    : "nowrap",
                lineHeight: 1,
              }}
            >
              {total ? maskValue(total) : total || "0"}
            </Typography>
            <Typography
              color="text.secondary"
              sx={{
                fontSize: isMobile ? "0.7rem" : "0.65rem",
                mt: 0.3,
                wordBreak: "break-word",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: isSmallDesktop && !isTablet ? "none" : "-webkit-box",
                WebkitLineClamp: isMobile ? 2 : 1,
                WebkitBoxOrient: "vertical",
              }}
            >
              {subtitle}
            </Typography>
          </Box>

          {/* Data items section */}
          {data && data.length > 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection:
                  data.length > 3 && isSmallDesktop ? "row" : "column",
                flexWrap: data.length > 3 && isSmallDesktop ? "wrap" : "nowrap",
                gap: data.length > 3 && isSmallDesktop ? 0.3 : 0.4,
                alignItems:
                  isMobile || (isSmallDesktop && data.length > 2)
                    ? "flex-start"
                    : "flex-end",
                flexShrink: 0,
                maxWidth:
                  isMobile || (isSmallDesktop && data.length > 2)
                    ? "100%"
                    : "55%",
                minWidth:
                  isMobile || (isSmallDesktop && data.length > 2)
                    ? "100%"
                    : "100px",
              }}
            >
              {data.map((item, index) => (
                <Box
                  key={index}
                  display="flex"
                  alignItems="center"
                  sx={{
                    width: data.length > 3 && isSmallDesktop ? "48%" : "100%",
                    justifyContent:
                      isMobile || (isSmallDesktop && data.length > 2)
                        ? "flex-start"
                        : "flex-end",
                    minWidth: 0,
                  }}
                >
                  <Box
                    width={isSmallDesktop ? 6 : 8}
                    height={isSmallDesktop ? 6 : 8}
                    borderRadius="50%"
                    bgcolor={item.color}
                    mr={0.4}
                    flexShrink={0}
                  />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      fontSize: isMobile
                        ? "0.65rem"
                        : isSmallDesktop
                        ? "0.66rem"
                        : "0.72rem",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      minWidth: 0,
                      lineHeight: 1.3,
                    }}
                  >
                    {item.label} ({maskValue(item.value)})
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default MetricCard;
